import React, {useEffect, useRef, useState} from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {Form} from '@unform/web';
import {Input as UInput} from '../../components/input';
import * as Yup from 'yup';
import * as moment from 'moment';
import ReactSelect from '../../components/react-select';

import './user.sass';
import ToggleButton from '../../components/toggle-button';
import {generateToken} from '../../helpers/auth';
import I18nMessage from '../../components/I18nMessage';
import DatePicker from '../../components/react-datepicker';
import {FieldSet} from '../../components/fieldset';
import {GroupButtonForm} from '../../components/group-button-form';
import ReactMaskPhone from '../../components/react-mask-phone';
import IntegrationAccessToggles from '../../autobahn/features/integration/access-toggles';
import {useIntegrations} from '../../autobahn/features/integration/queries';

const schema = Yup.object().shape({
    id: Yup.number().notRequired(),
    customerId: Yup.number().notRequired(),
    name: Yup.string().required(),
    email: Yup.string().required(),
    login: Yup.string().nullable().notRequired(),
    password: Yup.string().nullable().notRequired(),
    phone: Yup.string().notRequired(),
    map: Yup.string().nullable().notRequired(),
    latitude: Yup.number().notRequired(),
    longitude: Yup.number().notRequired(),
    zoom: Yup.number().notRequired(),
    twelveHourFormat: Yup.boolean().notRequired(),
    coordinateFormat: Yup.string().nullable().notRequired(),
    poiLayer: Yup.string().nullable().notRequired(),
    disabled: Yup.boolean().notRequired(),
    administrator: Yup.boolean().notRequired(),
    readonly: Yup.boolean().notRequired(),
    deviceReadonly: Yup.boolean().notRequired(),
    limitCommands: Yup.boolean().notRequired(),
    expirationTime: Yup.string().notRequired().nullable(), // "2019-10-31T03:00:00.000+0000"
    deviceLimit: Yup.number().notRequired(),
    userLimit: Yup.number().notRequired(),
    token: Yup.string().nullable().notRequired(),
    attributes: Yup.object().shape({
        keyword: Yup.string().nullable().notRequired(),
        timezone: Yup.string().nullable().notRequired(),
    }),
});

const cast = (src) => {
    const data = {...src};

    data.attributes = data.attributes ? {...data.attributes} : {};

    data.userLimit = data.userLimit ?? 0;
    data.zoom = data.zoom ?? 0;
    data.expirationTime = data.expirationTime ?? undefined;
    data.deviceLimit = data.deviceLimit ?? 0;
    data.latitude = data.latitude ?? 0;
    data.longitude = data.longitude ?? 0;

    return data;
};

const UserForm = ({
    cancel,
    data,
    edit,
    newRegister,
    updateRegister,
    coordinateFormats,
    profile,
    loading,
    userStore,
    customerStore,
    timezoneStore,
    canGrantIntegrations = false,
    defaultCustomerId,
}) => {
    const formRef = useRef(null);
    const customerOptions = customerStore.listAsOptions;
    const timezoneOptions = timezoneStore.listAsOptions;

    // Central normalmente cria usuário sem escolher Customer -- esse é o vínculo básico, não uma
    // exceção. O campo serve pra adicionar um cliente por cima, não pra decidir se há vínculo.
    // MON-566: customerId de usuário órfão chega da API como 0 (campo long primitivo no backend,
    // nunca null), então `data?.customerId ?? defaultCustomerId` nunca caía no default -- `0 ?? x`
    // é `0`. Tratar 0 como ausente pra herdar defaultCustomerId igual um customerId de verdade faria.
    const initialCustomerId = data?.customerId > 0 ? data.customerId : defaultCustomerId;
    // A central escolhida decide quais integrações podem ser liberadas para este usuário.
    const [centralId, setCentralId] = useState(initialCustomerId ?? 0);
    // Na criação o usuário ainda não tem id: a seleção espera aqui e vira vínculo depois do Salvar.
    const [pendingIntegrations, setPendingIntegrations] = useState([]);

    // Pelo customer escolhido, e nao pelo que ja foi liberado a ele: o servidor resolve a central
    // que governa a cadeia. Cliente gerenciado nunca recebe liberacao propria, entao filtrar pelo
    // vinculo dele deixava a lista vazia e escondia o painel justo para quem precisava dele.
    const {data: availableIntegrations} = useIntegrations(
        centralId > 0 ? {all: true, customerId: centralId} : {all: true});
    const showIntegrations = canGrantIntegrations && centralId > 0 && availableIntegrations.length > 0;

    const handleSubmit = async (formData) => {
        const userData = {...formData};
        // Central normalmente cria/edita usuario sem escolher Customer -- vinculo automatico
        // (MON-566) preservado mesmo com o select nascendo vazio: so reaplica o backfill quando
        // o operador de fato nao tocou no campo (continua 0/ausente no submit).
        if (!(userData.customerId > 0)) {
            userData.customerId = initialCustomerId;
        }
        if (data.attributes) {
            userData.attributes = {
                ...data.attributes,
                ...userData.attributes,
            };
        }
        let user = {
            ...data,
            ...userData,
        };

        try {
            if (user.expirationTime) {
                user.expirationTime = moment(user.expirationTime).toISOString();
            }

            user = schema.cast(user);

            await schema.validate(user, {
                strict: true,
                abortEarly: false,
                stripUnknown: true,
            });

            edit ? updateRegister(user) : newRegister(user, pendingIntegrations);
        } catch (err) {
            console.log(err);
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    const setToken = () => {
        formRef.current.setFieldValue('token', generateToken());
    };

    useEffect(() => {
        if (edit && data?.id) {
            userStore.revalidateUser(data.id);
        }
    }, [edit]);

    return (
        <>
            <Form ref={formRef} onSubmit={handleSubmit}
                initialData={cast({...data, customerId: data?.customerId > 0 ? data.customerId : null})}>

                {!!edit && <UInput hidden type="number" name="id"/>}

                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIdentification"/>}>

                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedName"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="name"
                                    id="name"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userEmail"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="email"
                                    name="email"
                                    id="email"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="login"><I18nMessage id="sharedLogin"/></Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="login"
                                    id="login"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="login"><I18nMessage id="sharedKeyword"/></Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="attributes.keyword"
                                    id="attributes.keyword"
                                />
                            </FormGroup>
                        </div>

                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="phone"><I18nMessage id="sharedPhone"/></Label>
                                <ReactMaskPhone
                                    tag={UInput}
                                    type="number"
                                    name="phone"
                                    id="phone"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userPassword"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="password"
                                    name="password"
                                    id="password"
                                    autoComplete={'new-password'}
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedCustomer" />*</Label>
                                <ReactSelect
                                    id={'customerId'}
                                    name="customerId"
                                    onChange={(option) => setCentralId(option?.id ?? 0)}
                                    options={customerOptions} />
                            </FormGroup>
                                <FormGroup className="custom-formGroup">
                                    <Label for="name"><I18nMessage id="sharedTimezone" /></Label>
                                    <ReactSelect
                                        id={'attributes.timezone'}
                                        name="attributes.timezone"
                                        options={timezoneOptions} />
                                </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                <br/>
                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedPermissions"/>}>
                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="sharedDisabled"/></Label>
                                <ToggleButton name={'disabled'} id={'disabled'}/>
                            </FormGroup>
                            {profile === 'admin' &&
                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="userAdmin"/></Label>
                                <ToggleButton name={'administrator'} id={'administrator'}/>
                            </FormGroup>
                            }
                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="serverReadonly"/></Label>
                                <ToggleButton name={'readonly'} id={'readonly'}/>
                            </FormGroup>
                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="userDeviceReadonly"/></Label>
                                <ToggleButton name={'deviceReadonly'} id={'deviceReadonly'}/>
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="userLimitCommands"/></Label>
                                <ToggleButton name={'limitCommands'} id={'limitCommands'}/>
                            </FormGroup>

                            <FormGroup className="custom-formGroup d-flex align-items-center justify-content-between mb-4">
                                <Label className="mb-0 mr-3"><I18nMessage id="settingsTwelveHourFormat"/></Label>
                                <ToggleButton name={'twelveHourFormat'} id={'twelveHourFormat'}/>
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                {showIntegrations && (
                    <>
                        <br/>
                        <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIntegrations"/>}>
                            <div className="row">
                                <div className={'col'}>
                                    <IntegrationAccessToggles
                                        owner="user"
                                        ownerId={edit ? data?.id : undefined}
                                        integrations={availableIntegrations}
                                        value={pendingIntegrations}
                                        onChange={setPendingIntegrations}
                                    />
                                </div>
                            </div>
                        </FieldSet>
                    </>
                )}
                <br/>
                <FieldSet className="text-left border p-4" title={<I18nMessage id="settingsTitle"/>}>
                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="positionLatitude"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="latitude"
                                    id="latitude"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="positionLongitude"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="longitude"
                                    id="longitude"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="serverZoom"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="zoom"
                                    id="zoom"
                                />
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="settingsCoordinateFormat"/></Label>
                                <ReactSelect
                                    id={'coordinateFormat'}
                                    name="coordinateFormat"
                                    options={coordinateFormats}/>
                            </FormGroup>
                            {(profile === 'admin' || profile === 'monitor') &&
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userDeviceLimit"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="deviceLimit"
                                    id="deviceLimit"
                                />
                            </FormGroup>
                            }
                            {(profile === 'admin' || profile === 'monitor') &&
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userUserLimit"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="userLimit"
                                    id="userLimit"
                                />
                            </FormGroup>
                            }
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userExpirationTime"/></Label>
                                <DatePicker
                                    name={'expirationTime'}
                                    displayFormat={'dd/MM/yyyy'}
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="token"><I18nMessage id="userToken"/></Label>
                                <div className="input-group mb-2">
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="token"
                                        id="token"
                                    />
                                        <div className="input-group-prepend">
                                            <div className="input-group-text token">
                                                <span color="primary" onClick={() => setToken()}>
                                                    <i className="fas fa-sync-alt"/>
                                                </span>
                                            </div>
                                        </div>
                                </div>
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                <br/>
                <GroupButtonForm
                    loading={loading}
                    cancel={cancel}
                    submit={() => formRef.current.submitForm()}
                />
            </Form>
        </>
    );
};

export default UserForm;
